import { PrimitiveAtom, useSetAtom } from 'jotai';
import { Alert, AlertColor } from '@mui/material';
import { SnackbarMessage } from '@/types';
import { snackbarMessage as TemplateBuilderSnackbarMessage } from './state';
import {
  RemediationScoreConfig,
  RemediationScoreAreaField,
  RemediationScorePoiField,
  RemediationScoreCofField,
  ReportConfig,
  CardConfig,
  Equation,
  Expression,
  ExpressionType,
  SubtitleConfig,
  Variable,
  MathOperator,
  QaqcSectionConfig,
  LogsTemplateField,
} from '@/__generated__/graphql';
import { NEW_FIELD_PREFIX, REPORT_CONFIG_KEYS } from './constants';

import { toSnakeCase } from '@/utils/string';

type Props = {
  atom?: PrimitiveAtom<SnackbarMessage>;
  variant?: 'filled' | 'standard' | 'outlined';
};

export const useSnackBarMessage = ({
  atom = TemplateBuilderSnackbarMessage,
  variant = 'filled',
}: Props) => {
  const setSnackbarMessage = useSetAtom(atom);
  const showSnackBar = (message: string, severity: AlertColor) => {
    setSnackbarMessage({
      shouldShow: true,
      content: (
        <Alert sx={{ color: 'white' }} variant={variant} severity={severity}>
          {message && (
            <>
              {message}
              &nbsp;&nbsp;
            </>
          )}
        </Alert>
      ),
    });
  };

  return {
    showSnackBar,
  };
};

export const stripTypenameFromRemediationConfig = (
  remediationScoreConfig: RemediationScoreConfig
): RemediationScoreConfig => {
  return {
    area: remediationScoreConfig.area
      ? {
          fields: remediationScoreConfig.area.fields?.map((field: RemediationScoreAreaField) => ({
            fieldKey: field.fieldKey,
            threshold: field.threshold,
            score: field.score,
          })),
        }
      : null,
    poi: remediationScoreConfig.poi
      ? {
          fields: remediationScoreConfig.poi.fields?.map((field: RemediationScorePoiField) => ({
            poiTemplateKey: field.poiTemplateKey,
            poiScore: field.poiScore,
            subcategory: field.subcategory
              ? {
                  fieldKey: field.subcategory.fieldKey,
                  optionScores: field.subcategory.optionScores,
                }
              : undefined,
          })),
        }
      : null,
    cof: remediationScoreConfig.cof
      ? {
          fields: remediationScoreConfig.cof.fields?.map((field: RemediationScoreCofField) => ({
            fieldKey: field.fieldKey,
            optionScores: field.optionScores,
          })),
        }
      : null,
  };
};

export const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });

export const orderReportConfig = (config: ReportConfig['configurations']) => {
  const keyIndex: Record<string, number> = REPORT_CONFIG_KEYS.reduce(
    (accumulator, key, index) => ({ ...accumulator, [key]: index }),
    {}
  );

  return [...(config ?? [])].sort((a, b) => {
    const ai = keyIndex[a.key] ?? Infinity;
    const bi = keyIndex[b.key] ?? Infinity;
    return ai - bi;
  });
};

export const removeTypename = <T,>(obj: T): T => {
  if (Array.isArray(obj)) {
    return (obj as unknown[]).map((item) => removeTypename(item)) as unknown as T;
  }

  if (obj !== null && typeof obj === 'object') {
    if (obj instanceof Date) return obj;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...rest } = obj as Record<string, unknown>;

    const cleaned = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [key, removeTypename(value as unknown)])
    );

    return cleaned as T;
  }

  return obj;
};

export const titleToKey = (title: string) => title.trim().toLowerCase().replace(/\s+/g, '_');

export const keyFromTitle = (title: string) =>
  title
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9\s-_]/g, '')
    .replaceAll(/\s+/g, '_')
    .replaceAll(/_+/g, '_');

const cleanExpression = (expression: Expression | null | undefined): Expression | null => {
  if (!expression) return null;

  const cleaned = removeTypename(expression);

  if (cleaned.args) {
    cleaned.args = cleaned.args.map(cleanExpression).filter(Boolean) as Expression[];
  }

  if (cleaned.type === ExpressionType.Number) {
    delete cleaned.operator;
    delete cleaned.args;
    delete cleaned.variable;
    return cleaned;
  }

  if (cleaned.type === ExpressionType.Variable) {
    delete cleaned.operator;
    delete cleaned.args;
    delete cleaned.value;
    return cleaned;
  }

  if (cleaned.type === ExpressionType.Operation) {
    const validOperators = Object.values(MathOperator);
    const currentOperator = cleaned.operator as MathOperator;

    if (validOperators.includes(currentOperator)) {
      cleaned.operator = currentOperator;
    } else {
      cleaned.operator = MathOperator.Add;
    }

    cleaned.args = cleaned.args ?? [];

    delete cleaned.value;
    delete cleaned.variable;

    return cleaned;
  }

  return cleaned;
};

const cleanVariable = (variable: Variable | null | undefined): Variable | null => {
  if (!variable) return null;

  return removeTypename(variable);
};

const cleanEquation = (equation: Equation | null | undefined): Equation | null => {
  if (!equation) return null;

  const cleaned = removeTypename(equation);

  cleaned.expression = cleanExpression(cleaned.expression);
  cleaned.variables = cleaned.variables
    ? (cleaned.variables.map(cleanVariable).filter(Boolean) as Variable[])
    : [];

  return cleaned;
};

const cleanSubtitleConfig = (
  subtitleConfig: SubtitleConfig | null | undefined
): SubtitleConfig | null => {
  if (!subtitleConfig) return null;

  return removeTypename(subtitleConfig);
};

const cleanCardConfig = (card: CardConfig | null | undefined): CardConfig | null => {
  if (!card) return null;

  const cleaned = removeTypename(card);

  cleaned.subtitleConfig = cleanSubtitleConfig(cleaned.subtitleConfig);

  return cleaned;
};

export const cleanEquations = (equations: (Equation | null | undefined)[]): Equation[] => {
  return equations.map(cleanEquation).filter(Boolean) as Equation[];
};

export const cleanCardConfigs = (cards: (CardConfig | null | undefined)[]): CardConfig[] => {
  return cards.map(cleanCardConfig).filter(Boolean) as CardConfig[];
};

export const isNewQaqcField = (field: LogsTemplateField | undefined): boolean => {
  return Boolean(field?.id?.startsWith(NEW_FIELD_PREFIX));
};

export const cloneFields = (fields: LogsTemplateField[] | undefined): LogsTemplateField[] => {
  return structuredClone(fields ?? []);
};

export const buildUniqueFieldKey = (
  name: string,
  fields: LogsTemplateField[],
  selfId: string
): string => {
  const otherKeys = new Set(
    fields
      .filter((f) => f.id !== selfId)
      .map((f) => f.key)
      .filter(Boolean)
  );
  let base = toSnakeCase(name).replaceAll(/[^a-z0-9_]/gu, '') || 'field';
  if (!base) {
    base = 'field';
  }
  let key = base;
  let index = 0;
  while (otherKeys.has(key)) {
    index += 1;
    key = `${base}_${index}`;
  }
  return key;
};

export const filterIncomingOptions = (
  existing: string[],
  incoming: string[]
): { accepted: string[]; skipped: number } => {
  const lower = new Set(existing.map((option) => option.toLowerCase()));
  const accepted: string[] = [];
  let skipped = 0;
  for (const raw of incoming) {
    const trimmed = raw.trim();
    if (!trimmed) {
      continue;
    }
    if (lower.has(trimmed.toLowerCase())) {
      skipped += 1;
      continue;
    }
    lower.add(trimmed.toLowerCase());
    accepted.push(trimmed);
  }
  return { accepted, skipped };
};

export const normalizeQaqcConfig = (raw: unknown): QaqcSectionConfig[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((section: any) => ({
    id: section.id,
    sectionKey: section.sectionKey ?? '',
    title: section.title ?? '',
    order: Number(section.order) || 0,
    defaultOpen: section.defaultOpen ?? true,
    allowsMultipleSectionBlocks:
      section.allowsMultipleSectionBlocks ?? section.supportsMultipleEntries ?? false,
    tables: Array.isArray(section.tables)
      ? section.tables.map((table: any) => ({
          id: table.id,
          tableKey: table.tableKey ?? '',
          title: table.title ?? '',
          fieldKeys: Array.isArray(table.fieldKeys) ? [...table.fieldKeys] : [],
          order: Number(table.order) || 0,
          supportsMultipleEntries: table.supportsMultipleEntries ?? false,
        }))
      : [],
  }));
};

export const sortQaqcSectionsByOrder = <T extends { order: number }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.order - b.order);
};

export const assignQaqcSectionOrderFromArray = (
  sectionsInDisplayOrder: QaqcSectionConfig[]
): QaqcSectionConfig[] => {
  return sectionsInDisplayOrder.map((section, index) => ({
    ...section,
    order: index + 1,
    tables: sortQaqcSectionsByOrder(section.tables).map((table, tableIndex) => ({
      ...table,
      order: tableIndex + 1,
    })),
  }));
};

export const renumberQaqcSections = (sections: QaqcSectionConfig[]): QaqcSectionConfig[] => {
  return assignQaqcSectionOrderFromArray(sortQaqcSectionsByOrder(sections));
};
